import React, { useState, useEffect } from "react";
import { Toaster } from 'react-hot-toast';
import { Route, Redirect, Switch, useHistory, useLocation } from "react-router";
import _refiner from 'refiner-js';
import Layout from "./components/Layout";
import { StyledEngineProvider } from "@mui/material/styles";
import useUser from "./context/User/useUser";
import useNavBar from "./context/NavBar/useNavBar";

import "./custom.css";
import {
	Account,
	AccountDetails,
	Associations,
	// AddServiceProvider,
	ServiceProviders,
} from "./components/Account";
import {
	GeekCommandCenter,
	ManageServiceProviders,
	LinkServiceProviders,
	ConfigureSSO,
	ServiceProvidersLinkedLocations,
	ManageAccounts,
	ManageAllUsers,
	SiteUsage,
} from "./components/GeekCommandCenter";
import AddAsset from "./components/AddAsset/index";
import AddLocation from "./components/Location/AddLocation";
import AddOrEditServiceEvent from "./components/ServiceEvents";
import { AddWorkOrder } from "./components/AddWorkOrder";
import SiteLeakInspection from "./components/WorkOrder/SiteLeakInspection/index.js";
import AssetDetails from "./components/AssetDetails";
import HomePage from "./components/Dashboard/HomePage.js";
import ComplianceCenter from "./components/Dashboard/ComplianceCenter";
import Inventory from "./components/Inventory";
import MoveCylinders from "./pages/Inventory/MoveCylinders";
import Locations from "./components/Location/Locations";
import LocationView from "./components/Location/LocationView";
import AddLocationAudit from "./components/Location/Audit/index";
import Login from "./components/Login/Login";
import ResetPassword from "./components/ResetPassword";
import PowerBI from "./components/PowerBI";
import ServiceEventDetails from "./components/ServiceEvents/ServiceEventDetails";
import Routes from "./routes";
import UserInfo from "./UserInfo";
import Users from "./components/Account/Users";
// import { default as UserDetail } from "./components/Account/UserDetail";
// import Profile from "./components/Profile/Profile";
// import Workflow from "./components/Workflow";
import WorkOrders from "./components/WorkOrders";
import WorkOrderDetails from "./components/WorkOrderDetails";
import LoadingBar from "./components/Common/LoadingBar";
import AirFilterChangeOut from "./components/WorkOrder/Components/AirFilterChangeOut";
import { IntlProvider } from "react-intl";
import Styling from "./components/Styling";
import LanguageDemo from "./components/TranslationDemo";
import Translation_EN from "./Utils/Languages/English.json";
import Translation_SP from "./Utils/Languages/Spanish.json";
import Translation_FR from "./Utils/Languages/French.json";
import Translation_HI from "./Utils/Languages/Hindi.json";
import ServiceEvents from "./components/ServiceEvents/ServiceEvents";
import V4Disabled from "./components/V4Disabled";
import WelcomeModal from "./components/Dashboard/WelcomeModal";
import { hotjar } from "react-hotjar";
import UI from "./Icons/UI.gif";
import Rocket from "./Icons/rocket.gif";
import Search from "./Icons/search.gif";
import Click from "./Icons/click.gif";
import Construction from "./Icons/constructions.gif";
import LanguageContext from "./LanguageContext";
import Error404 from "./components/Common/Error/Error404";
import Error500 from "./components/Common/Error/Error500";
import ProfilePage from "./pages/Profile/Profile";
import EditProfile from "./pages/Profile/EditProfile";
import UserForm from "./components/Account/UserForm";
import User from "./components/Account/User";
// import SiteAirFilterChangeOut from "./components/WorkOrder/SiteAirFilterChangeOut";
import SiteAir from './components/WorkOrder/SiteAir';
import Documents from "./components/Account/Documents";
import AccountDetailsForm from "./components/Account/AccountDetailsForm";
import { AppRoles, isDesktop, isMobile, postRequestHeaders } from "./utils";

import MaterialTransfers from "./components/Inventory/MaterialTransfers/MaterialTransfers";
import RecordApplianceDisposals from "./components/Inventory/ApplianceDisposals/RecordApplianceDisposals";
import ApplianceDisposalTable from "./components/Inventory/Tables/ApplianceDisposalTable";
import ApplianceDisposals from "./components/Inventory/ApplianceDisposals/ApplianceDisposal";
import useBreadCrumbs from "./hooks/useBreadCrumbs";
import Classifications from "./components/Account/Classifications";

import ManageLocationRegistration from "./components/GeekCommandCenter/ManageLocationRegistration/ManageLocationRegistration";

import EmailAlerts from "./components/Account/EmailAlerts";
import { EmailAlertProvider } from "./components/Account/EmailAlerts/context/useEmailAlert";
import Analytics from "./components/PowerBI/Analytics";
import ManageMaterialTypes from "./components/GeekCommandCenter/ManageMaterialTypes/ManageMaterialTypes";
import ManageManufacturers from "./components/GeekCommandCenter/ManageManufacturers/ManageManufacturers";
import AddManufacturer from "./components/GeekCommandCenter/ManageManufacturers/AddManufacturer";
import SystemManagersPage from "./components/Account/SystemManagers/SystemManagerPage";
import LinkCoolingAppliances from "./components/Inventory/LinkCoolingAppliances/LinkCoolingAppliances";
import SelectAccount from "./components/SelectAccount";
import ViewMaterialTypes from "./components/Account/ViewMaterialType";
import { useIdleTimer } from "./hooks/useIdleTimer";
import SearchResults from "./components/SearchResults";
import WebHookLogs from "./components/Account/WebHookLogs";
import useOnlineStatus from "./hooks/useOnlineStatus";
import InsightsPage from "./pages/Insights";
import Dashboard from "./components/Dashboard/Dashboard.js";

const Translation = {
	"en-US": Translation_EN,
	"es-ES": Translation_SP,
	"fr-FR": Translation_FR,
	"hi-HI": Translation_HI,
};

const welcomeModalSteps = [
	{
		gif: UI,
		text1:
			"We've completely redesigned the product and simplified your favorite features.",
		showHeader: true,
	},
	{
		gif: Rocket,
		text1: "Fasten your seatbelt.",
		text2: "Trakref is faster than it's ever been! ??",
	},
	{
		gif: Search,
		text1: "Search for anything -- and anywhere.??",
		text2:
			"Use the search box to find work orders, service events, locations and inventory.",
	},
	{
		gif: Click,
		text1: "You asked for simpler, cleaner navigation. And we listened!",
		text2:
			"Now you can easily navigate to your work orders, service events, inventory, locations and reports. ??",
	},
	{
		gif: Construction,
		text1: "The new Trakref still doesn't have all the features available.",
		text2: "To find all the features, log in to Trakref Classic.",
	},
	{
		video: true,
		showHeader: true,
	},
];

const App = () => {
    // Change the second parameter (REFINER_PROJECT_ID) when ready 
    _refiner('setProject', 'f0eb99c0-cadb-11f0-a88a-09d935bab2e5');
	const userOnResetPasswordPage = window.location.pathname.toLowerCase().includes("resetpassword");
	const toastStyle = { position: "fixed", top: 120 };
	const history = useHistory();
	const location = useLocation();
	const [loading, setLoading] = useState(true);
	const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
	const [welcomeModalStep, setWelcomeModalStep] = useState(0);
	const [isMobleDevice, setIsMobleDevice] = useState(false);
	const [newInstance, setNewInstance] = useState(null);
	const [currentLanguage, setCurrentLanguage] = useState(
		UserInfo.culture || null
	);

	const breadCrumbs = useBreadCrumbs({
		path: null
	});

    const contractorLitePermissions = (
        UserInfo.isNativeAccountServiceProvider() && (
            UserInfo.isTenantAdministrator()
            || UserInfo.isTechnicianManager()
            || UserInfo.isTechnician()
        )
    );

	const { loggedIn, setLoggedIn, logout } = useUser();
	const { changeInstance } = useNavBar();
	const isOnline = useOnlineStatus();
	const [alertProperties, setAlertProperties] = useState({
		render: false,
		customAlertStyle: {},
		text: "",
		type: "", // success, warning, or error
	});
    
    // DO NOT DELETE - I AM REFACTORING TO CLEAN UP APP LOGIC - Bryan A.
    const routes = [
        {
            path: "/EditApplianceDisposals/:id",
            Component: RecordApplianceDisposals,
            canAccess: () =>
                isDesktop &&
                (
                    UserInfo.isSystemAdministrator() ||
                    contractorLitePermissions ||
                    (
                        UserInfo.isNativeSystemOwner() && (
                            UserInfo.isAdminRole() || UserInfo.isTechnicianManager() || UserInfo.isTechnician()
                        )
                    )
                ),
        },
    ];

	const defaultRoute = () => {
		if (UserInfo.isServiceProvider() && UserInfo.isNativeUser())
			return "/Users";

		if (UserInfo.isSystemAdministrator() ||
			UserInfo.isPurchasingAdministrator() ||
			UserInfo.isTechnicianManager() ||
			UserInfo.isTechnician()) {
			return "/Home";
		}

		if (!UserInfo.isNativeAccountServiceProvider() && (UserInfo.isSuperTenantAdmin() || UserInfo.isRegionalAdministrator()) && isDesktop)
			return "/ComplianceCenter";

		if (UserInfo.isSustainabilityManager())
			return "/Reports";

		return "/WorkOrders";
	}

	const login = () => {
		const lastPageView =  localStorage.getItem("lastPageView")
		const logoutType = localStorage.getItem("logoutType");
		const lastInstanceId = localStorage.getItem("lastInstanceId");

		// Restore session after inactivity timeout
		if (logoutType === "inactivity" && lastPageView) {
			const targetInstanceId = lastInstanceId ? parseInt(lastInstanceId) : null;

			// Switch to previous instance if user still has access
			if (targetInstanceId && targetInstanceId !== UserInfo.instanceId && UserInfo.canServiceInstance(targetInstanceId)) {
				localStorage.setItem("pendingRestoration", "true");
				changeInstance(targetInstanceId);
				return;
			}

			history.push(lastPageView);
			localStorage.removeItem("lastPageView")
			localStorage.removeItem("lastInstanceId");
			localStorage.removeItem("logoutType");
			setLoggedIn(true);
			return 
		}

		localStorage.removeItem("lastInstanceId");
		localStorage.removeItem("pendingRestoration");

		if (UserInfo.userId != null && UserInfo.isTempPassword && UserInfo.passwordResetCode) {
			history.push(`ResetPassword/${UserInfo.passwordResetCode}`);
		} 
		else if (UserInfo.userName != null && UserInfo.userId != null) {
			setLoggedIn(true);
			var initialRoute = defaultRoute();

			if (!isDesktop) {
				if ((UserInfo.isSystemAdministrator() || UserInfo.isNativeServiceProvider()) ) {	
					initialRoute = "/SelectAccount";
				}
				history.push(initialRoute);
				return;

			}

			if (UserInfo.isServiceProvider() && UserInfo.instanceId === UserInfo.nativeInstanceId) {
				history.push("/Users");
				return;
			}
			history.push(initialRoute);
		}
	};

	const showDemoVideo = () => {
		setWelcomeModalStep(5);
		return setWelcomeModalOpen(true);
	};

	const setInstanceWithoutLoad = (instanceId) => {
		setNewInstance(instanceId);
	};

	const setAlert = (new_obj) => setAlertProperties((prevProperties) =>
		({ ...prevProperties, ...new_obj })
	);

	const closeAndResetAlert = () => setAlertProperties({
		render: false,
		customAlertStyle: {},
		text: "",
		type: "",
	});

	useIdleTimer({ enable: loggedIn, onIdle: logout, timeout: UserInfo.idleTimeout });
    useEffect(() => {
        const handleStorage = (event) => {
            // Logout was triggered in a different tab
            if (event.key === 'logout') {
                UserInfo.reset();
                setLoggedIn(false);
                history.push("/Login");
            }
        };

        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    useEffect(() => {
        const handleStorage = (event) => {
            // Logout was triggered in a different tab
            if (event.key === 'logout') {
                UserInfo.reset();
                setLoggedIn(false);
                history.push("/Login");
            }
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

	useEffect(() => {
		if (isMobile) {
			if (!isMobleDevice) {
				setIsMobleDevice(true);
			}
			history.push("");
		}
		const fetchData = async () => {
			const response = await fetch(Routes.Authentication.CheckAuthenticated(), {
				method: "POST",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
			});
			if (!response.url.includes("Login") && response.status === 200) {
				const data = await response.json();
				// console.log('login data', data);

				const instance = data.instances.find(x => x.id === data.instanceId);

				const isProduction = (localStorage.getItem("isprod") === 'true');
				UserInfo.setIsProduction(isProduction);
				UserInfo.setFullPayload(data);
				UserInfo.setUserName(data.userName);
				UserInfo.setUserId(data.id);
				UserInfo.setAccountType(data.accountTypeId);
				UserInfo.setFirstName(data.firstName);
				UserInfo.setLastName(data.lastName);
				UserInfo.setInstanceId(data.instanceId);
				UserInfo.setNativeInstanceId(data.nativeInstanceId);
				UserInfo.setInstances(data.instances);
				UserInfo.setInstanceName(instance.name);
				UserInfo.setLocations(data.locations);
				UserInfo.setGroupId(data.groupId);
				UserInfo.setUserCulture(data.culture);
				UserInfo.setWeightUnits(data.weightUnits);
				UserInfo.setIsOptedIn(data.isOptedIn);
				UserInfo.setLbsOnlyDecimal(data.onlyWeightDecimal);
				UserInfo.setIsWriteIntech(data.isWriteInTech);
				UserInfo.setRoles(data.roles);
				UserInfo.setNativeAccountTypeId(data.nativeAccountTypeId);
				UserInfo.setAllowAnalytics(instance.allowAnalytics);
				UserInfo.setIdleTimeout(data.idleTimeout);
				UserInfo.setLoginTypeId(data.loginTypeId);
				
				if (data?.isTempPassword && data?.passwordResetCode) {
					history.push(`ResetPassword/${data.passwordResetCode}`);
				} else {
					setCurrentLanguage(data.culture);

					// Restore previous page after instance switch
					const pendingRestoration = localStorage.getItem("pendingRestoration");
					const lastPageView = localStorage.getItem("lastPageView");

					if (pendingRestoration === "true" && lastPageView) {
						localStorage.removeItem("pendingRestoration");
						localStorage.removeItem("lastPageView");
						localStorage.removeItem("lastInstanceId");
						history.push(lastPageView);
					}

					setLoggedIn(true);
				}
				
			}
			setLoading(false);
		};

		const determineWelcomeModal = () => {
			const welcomeModalSeen = localStorage.getItem("welcomeModalSeen");
			if (!welcomeModalSeen) {
				setWelcomeModalOpen(true);
			}
		};

		localStorage.setItem(
			"isprod",
			document.URL.includes("v4.trakref.com") ||
			document.URL.includes("productiontrakref-staging.azurewebsites.net")
		);

		fetchData();
		determineWelcomeModal();
		hotjar.initialize(3315058, 6);
		breadCrumbs.clearBreadCrumbs(true);
	}, []);

	useEffect(() => {
		const recordPageView = async () => {

			const getBrowser = () => {
				if (
					window.navigator
					&& window.navigator.userAgentData
					&& !!window.navigator.userAgentData.brands.length
				) {
					return `${window.navigator.userAgentData.brands[0].brand} ${window.navigator.userAgentData.brands[0].version}`.trim();
				}

				return 'Could not detect browser';
			};

			await fetch(Routes.Account.RecordPageView(), {
				method: "POST",
				headers: { ...postRequestHeaders },
				body: JSON.stringify({
					host: window.location.host,
					page: history.location.pathname,
					pathAndQuery: history.location.pathname,
					referrer: document.referrer,
					userAgent: window.navigator.userAgent,
					browser: getBrowser(),

				})
			});
		};

		recordPageView();
	}, [location.pathname]);

	if (loading) {
		return <LoadingBar stylish />;
	}

	if (loggedIn && userOnResetPasswordPage) {
		const resetPasswordURL = window.location.href;
		return logout(resetPasswordURL, true);
	}

	if (!loggedIn) {
		return (
			<>
				<StyledEngineProvider injectFirst>
					<Toaster containerStyle={toastStyle} />
					<Switch>
						<Route
							exact
							path="/Login"
							render={() => (
								<Login
									onChange={() => login()}
									setCurrentLanguage={setCurrentLanguage}
								/>
							)}
						/>
						<Route
							exact
							path="/ResetPassword/:guid"
							render={(props) => <ResetPassword {...props} />}
						/>
						<Route
							exact
							path="/*"
							render={() => (
								<Login
									onChange={() => login()}
									setCurrentLanguage={setCurrentLanguage}
								/>
							)}
						/>
					</Switch>
				</StyledEngineProvider>
			</>
		);
	}

	if (loggedIn && UserInfo.isOptedIn !== true) {
		return (
			<LanguageContext.Provider value={{ currentLanguage, setCurrentLanguage }}>
				<IntlProvider
					locale={currentLanguage}
					messages={Translation[currentLanguage]}
				>
					<StyledEngineProvider injectFirst>
						<Layout logout={logout} newInstance={newInstance}>
							<Route path="*" component={V4Disabled} />
						</Layout>
						</StyledEngineProvider>
				</IntlProvider>
			</LanguageContext.Provider>
		);
	}

	return (
		<>
			<LanguageContext.Provider value={{ currentLanguage, setCurrentLanguage }}>
				<IntlProvider
					locale={currentLanguage}
					messages={Translation[currentLanguage]}
				>
					<StyledEngineProvider injectFirst>
						<Toaster containerStyle={toastStyle} />
						<Switch>
							{<Route path="/SelectAccount" component={SelectAccount} />}
						<Layout
							logout={logout}
							newInstance={newInstance}
							showDemoVideo={showDemoVideo}
							renderAlert={alertProperties.render}
							alertText={alertProperties.text}
							alertType={alertProperties.type}
							customAlertStyle={alertProperties.customAlertStyle}
							setAlert={setAlert}
						>
							<Switch>
								<Route exact path="/Login">
									<Redirect to={defaultRoute()} />
								</Route>
								<Route
									exact
									path="/ResetPassword/:guid"
									render={(props) => <ResetPassword {...props} />}
								/>
								<Route exact path="/">
									<Redirect to={defaultRoute()} />
								</Route>
								<Route exact path="/index.html">
									<Redirect to={defaultRoute()} />
								</Route>
								<Route path="/Home" component={HomePage} />
								<Route path="/SearchResults" component={SearchResults} />
								{
									(!UserInfo.isNativeAccountServiceProvider() && (UserInfo.isReadOnly() || UserInfo.isSystemAdministrator() || UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator() || UserInfo.isRegionalAdministrator())) && (
										<Route path="/ComplianceCenter" component={ComplianceCenter} />
									)
								}
								{
									(!UserInfo.isNativeAccountServiceProvider() && (UserInfo.isReadOnly() || UserInfo.isSystemAdministrator() || UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator() || UserInfo.isRegionalAdministrator())) && (
										<Route path="/Dashboard" component={Dashboard} />
									)
								}
								<Route path="/Account" component={Account} />
								{
									(UserInfo.isSystemAdministrator()) && (
										<Route path="/GeekCommandCenter" component={GeekCommandCenter} />
									)
								}
								{
									UserInfo.isSystemAdministrator() &&
									<Route path="/SiteUsage" component={SiteUsage} />
								}

								<Route path="/ManageServiceProviders" component={ManageServiceProviders} />
								{
									((UserInfo.isSuperTenantAdmin() || UserInfo.isNativeTenantAdministrator() || UserInfo.isSystemAdministrator()) && !UserInfo.isServiceProvider()) &&
									<Route
									path="/MyServiceProviders"
									render={() => (
										<ManageServiceProviders
											pageTitle="My Service Providers"
											toolTipText={
												`View the service providers associated with your account,
												check their assigned restrictions and privileges,
												and allocate a service provider to a specific location.`
											}
											origin="Account Details"
										/>
									)}
								/>
								}
								<Route path="/ServiceProvidersLinkedLocations" component={ServiceProvidersLinkedLocations} />
								<Route path="/LinkServiceProviders" component={LinkServiceProviders} />
								<Route path="/ViewMaterialType" component={ViewMaterialTypes} />
								<Route path="/ManageMaterialTypes" component={ManageMaterialTypes} />
								<Route path="/ConfigureSSO" component={ConfigureSSO} />
								<Route path="/ViewMaterialType" component={ViewMaterialTypes} />
								<Route path="/ManageAllUsers" component={ManageAllUsers} />
								{UserInfo.isSystemAdministrator() &&
									<Route path="/Manufacturers" component={ManageManufacturers} />
								}
								{UserInfo.isSystemAdministrator() &&
									<Route path="/AddManufacturer" component={AddManufacturer} />
								}
								<Route path="/ManageAccounts" component={ManageAccounts} />
								{
									!(UserInfo.isServiceProvider() && UserInfo.isNativeUser()) &&
									<Route path="/ConfigureSSO" component={ConfigureSSO} />
								}
								{
									UserInfo.isNativeUser() &&
									<Route path="/Documents" component={Documents} />
								}
								{
									UserInfo.isSystemAdministrator() &&
									<Route path="/ManageRegistration" component={ManageLocationRegistration} />
								}
								{
									(UserInfo.isSystemAdministrator() || (UserInfo.isNativeSystemOwner() && (UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator()))) &&
									<Route
										path="/EmailAlerts"
										render={() => (
											<EmailAlertProvider>
												<EmailAlerts />
											</EmailAlertProvider>
										)}
									/>
								}
								{
									(
										UserInfo.isSystemAdministrator() || (
											UserInfo.isNativeSystemOwner() && !(
												UserInfo.isRegionalAdministrator()
												|| UserInfo.isTechnician()
												|| UserInfo.isTechnicianManager()
												|| UserInfo.isReadOnly()
											)
										) || (
											UserInfo.isNativeServiceProvider() && (
												UserInfo.isTenantAdministrator()
											)
										)
									) &&
									<Route path="/AccountDetails" component={AccountDetails} />
								}
								{
									UserInfo.isRoleExist(AppRoles.Tenant_Administrator_CurrentInstance) &&
									<Route path="/EditAccountDetails" component={AccountDetailsForm} />
								}
								{
									UserInfo.isRoleExist(AppRoles.Tenant_Administrator_CurrentInstance) &&
									<Route
										path="/Regions"
										render={(props) => (
											<Associations {...props} />
										)}
									/>
								}
								{
									(UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator() || UserInfo.isSystemAdministrator()) &&
									<Route
										path="/Classifications"
										render={(props) => (
											<Classifications
												{...props}
												setAlert={setAlert}
												closeAndResetAlert={closeAndResetAlert}
											/>
										)}
									/>
								}
								<Route path="/SystemManagers" component={SystemManagersPage} />
								<Route path="/WebHookLogs" component={WebHookLogs} />
								<Route
									exact
									path="/Inventory/:id"
									render={(props) => (
										<AssetDetails
											{...props}
											root="Inventory"
											setAlert={setAlert}
											closeAndResetAlert={closeAndResetAlert}
										/>
									)}
								/>
								<Route exact path="/Inventory" component={Inventory} />
								{
									(UserInfo.isSystemAdministrator() ||
										UserInfo.isNativeAccountServiceProvider() && (UserInfo.isTenantAdministrator() || UserInfo.isTechnicianManager() || UserInfo.isTechnician()) ||
										UserInfo.isNativeSystemOwner() && (UserInfo.isTechnicianManager() || UserInfo.isTechnician()) ||
										(UserInfo.isNativeUser() && (UserInfo.isAdminRole() || UserInfo.isRegionalAdministrator()))) && (
										<Route
											path="/AddInventory"
											render={(props) => (
												<AddAsset
													{...props}
													setAlert={setAlert}
													closeAndResetAlert={closeAndResetAlert}
												/>
											)}
										/>
									)
								}
								{


									(
										(UserInfo.isSystemAdministrator() || (UserInfo.isNativeUser() && (UserInfo.isAdminRole() || UserInfo.isRegionalAdministrator()))) ||
										UserInfo.isNativeSuperTenantAdministrator() ||
										UserInfo.isNativeTenantAdministrator() ||
										UserInfo.isNativeRegionalAdministrator()
									) && (
										<Route path="/EditInventory/:id" component={AddAsset} />
									)
								}
								{
									(UserInfo.isNativeAdminRole() || UserInfo.isNativeRegionalAdministrator()) && (
										<Route
											path="/LinkCoolingAppliances/:id"
											render={(props) => (
												<LinkCoolingAppliances {...props} />
											)}
										/>
									)
								}
								<Route
									exact
									path="/MoveCylinders"
									render={(props) => (
										<MoveCylinders
											{...props}
											setAlert={setAlert}
											closeAndResetAlert={closeAndResetAlert}
										/>
									)}
								/>
								<Route
									exact
									path="/SendCylindersToRecycling"
									render={(props) => (
										<MoveCylinders
											{...props}
											sendToRecycling
											setAlert={setAlert}
											closeAndResetAlert={closeAndResetAlert}
										/>
									)}
								/>
								<Route
									path="/Location/:id/CreateAudit"
									render={(props) => (
										<AddLocationAudit
											root="Locations"
											{...props}
											setAlert={setAlert}
											closeAndResetAlert={closeAndResetAlert}
										/>
									)}
								/>
								<Route
									path="/Location/:id"
									render={(props) => (
										<LocationView
											{...props}
											root="Locations"
											setAlert={setAlert}
											closeAndResetAlert={closeAndResetAlert} />
									)}
								/>
								{isDesktop && (
									<Route path="/Locations" component={Locations} />
								)}
								<Route path="/CreateLocation" component={AddLocation} />
								{
									(UserInfo.isSystemAdministrator() || (
										(
											UserInfo.isTenantAdministrator() ||
											UserInfo.isTechnicianManager() ||
											UserInfo.isSuperTenantAdmin()
										) && !UserInfo.isNativeAccountServiceProvider())
									) && (
										<Route
											path="/EditLocation/:id"
											render={() => <AddLocation isEditing />}
										/>
									)
								}
								{
									(
										UserInfo.allowAnalytics && 
										(UserInfo.isSystemAdministrator() || UserInfo.isNativeSystemOwner()) && 
										(
											 UserInfo.isAdminRole() || 
											 UserInfo.isRegionalAdministrator() ||
											 UserInfo.isReadOnly() ||
											 UserInfo.isSustainabilityManager()
										)
									) && (
										<Route
											path="/Analytics"
											render={() => (<Analytics />)}
										/>
									)
								}
								<Route
									path="/Insights"
									render={() => (<InsightsPage />)}
								/>
								<Route
									path="/TrakrefReports"
									render={() => (
										<PowerBI
											rls
											trakrefReports
											title="Reports"
											setAlert={setAlert}
											closeAndResetAlert={closeAndResetAlert}
										/>
									)}
								/>

								<Route path="/Reports/:name" component={PowerBI} />
								<Route path="/Reports/" component={PowerBI} />

								<Route path="/ServiceEvents" component={ServiceEvents} />
								<Route
									path="/ServiceEvent/:id"
									render={(props) => (
										<ServiceEventDetails
											{...props}
											setAlert={setAlert}
											closeAndResetAlert={closeAndResetAlert}
										/>
									)}
								/>
								<Route
									path="/CreateServiceEvent/:workOrderId"
									render={() => (
										<AddOrEditServiceEvent
											setAlert={setAlert}
											closeAndResetAlert={closeAndResetAlert}
										/>
									)}
								/>
								{!UserInfo.isReadOnly() && (
									<Route
										path="/EditServiceEvent/:serviceEventId"
										render={() => (
											<AddOrEditServiceEvent
												isEditing
												setAlert={setAlert}
												closeAndResetAlert={closeAndResetAlert}
											/>
										)}
									/>
								)}
								{
									!UserInfo.isProduction && UserInfo.isRoleExist(AppRoles.Tenant_Administrator_CurrentInstance) &&
									<Route path="/ServiceProviders" component={ServiceProviders} />
								}
								{/* <Route
									path="/AddServiceProvider"
									component={AddServiceProvider}
								/> */}
								{
									UserInfo.isRoleExist(AppRoles.View_Users_CurrentInstance) &&
									<Route
										path="/Users/"
										render={() => (
											<Users
												setAlert={setAlert}
												closeAndResetAlert={closeAndResetAlert}
											/>
										)}
									/>
								}
								<Route
									path="/User/:id"
									render={() => (
										<User
											setAlert={setAlert}
											closeAndResetAlert={closeAndResetAlert}
										/>
									)}
								/>
								{
									UserInfo.isRoleExist(AppRoles.Can_Manage_User) && (
										<Route
											path="/UserAdd/"
											render={() => (
												<UserForm
													setAlert={setAlert}
													closeAndResetAlert={closeAndResetAlert}
												/>
											)}
										/>
									)
								}
								<Route
									path="/UserUpdate/:userId"
									render={() => (
										<UserForm
											setAlert={setAlert}
											closeAndResetAlert={closeAndResetAlert}
										/>
									)}
								/>
								<Route
									path="/WorkOrders"
									render={(props) => (
										<WorkOrders
											{...props}
											setAlert={setAlert}
											closeAndResetAlert={closeAndResetAlert}
										/>
									)}
								/>
								<Route
									path="/EditWorkOrder/:id"
									render={(props) => (
										<AddWorkOrder
											{...props}
											setAlert={setAlert}
											closeAndResetAlert={closeAndResetAlert}
										/>
									)}
								/>
								<Route
									path="/CreateWorkOrder"
									render={(props) => (
										<AddWorkOrder
											{...props}
											setAlert={setAlert}
											closeAndResetAlert={closeAndResetAlert}
										/>
									)}
								/>
								<Route path="/Profile">
									<Switch>
										<Route exact path="/Profile" component={ProfilePage} />
										{(UserInfo.isTenantAdministrator() || UserInfo.isSystemAdministrator()) && (
											<Route path="/Profile/Edit" component={EditProfile} />
										)}
										<Route component={Error404} />
									</Switch>
								</Route>
								{/* <Route path="/Workflow" component={Workflow} /> */}
								<Route
									exact
									path="/WorkOrder/:id"
									render={(props) => (
										<WorkOrderDetails
											{...props}
											setInstanceWithoutLoad={setInstanceWithoutLoad}
											root="Work Orders"
										/>
									)}
								/>
								<Route
									exact
									path="/WorkOrder/:id/SiteLeakInspection/"
									component={SiteLeakInspection}
								/>
								<Route
									path="/AirFilterChangeOut/:id"
									component={AirFilterChangeOut}
								/>
								{/* <Route exact
									path="/WorkOrder/:id/SiteAirFilterChangeOut/"
									component={SiteAirFilterChangeOut}
								/> */}
								<Route exact
									path="/WorkOrder/:id/SiteAir/"
									component={SiteAir}
								/>
								{isDesktop && <Route exact path="/MaterialTransfers" component={MaterialTransfers} />}
								{isDesktop && <Route exact path="/ApplianceDisposals" component={ApplianceDisposalTable} />}
								{isDesktop && <Route
									path="/ApplianceDisposals/:id"
									render={(props) => (
										<ApplianceDisposals {...props} root="Appliance Disposals" />
									)} />}
                                {isDesktop && (
                                    (
                                        UserInfo.isSystemAdministrator() ||
                                        contractorLitePermissions ||
                                        UserInfo.isNativeSystemOwner() && (
                                            UserInfo.isAdminRole() ||
                                            UserInfo.isTechnicianManager() ||
                                            UserInfo.isTechnician()
                                        )) && (
                                            <Route
                                                path="/EditApplianceDisposals/:id"
                                                render={(props) => (
                                                    <RecordApplianceDisposals {...props} />
                                                )}
                                            />
                                        )
                                    )
                                }
                                {isDesktop && (
                                    (
                                        UserInfo.isSystemAdministrator() ||
                                        contractorLitePermissions ||
                                        UserInfo.isNativeSystemOwner() && (
                                            UserInfo.isAdminRole() ||
                                            UserInfo.isRegionalAdministrator() ||
                                            UserInfo.isTechnicianManager() ||
                                            UserInfo.isTechnician()
                                        )) && (
                                            <Route
                                                path="/RecordApplianceDisposals/"
                                                render={(props) => (
                                                    <RecordApplianceDisposals {...props} />
                                                )}
                                            />
                                        )
                                    )
                                }
								<Route path="/Styling" component={Styling} />
								<Route path="/LangDemo" component={LanguageDemo} />
								<Route path="/Error404" component={Error404} />
								<Route path="/Error500" component={Error500} />
								<Route component={Error404} />
							</Switch>
						</Layout>
						</Switch>
						</StyledEngineProvider>
				</IntlProvider>
			</LanguageContext.Provider>
		</>
	);
};

export default App;
